import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isNative } from '../lib/platform';
import { PORTAL } from '../lib/portal';
import { useSession } from '../store/session';

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let adsenseLoaded = false;
function loadAdsense(client: string) {
  if (adsenseLoaded) return;
  adsenseLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
  document.head.appendChild(s);
}

/**
 * Banner ad for free users on menu screens only (never in-game).
 * Web: Google AdSense, loaded lazily. Without AdSense config (or on native,
 * where AdMob interstitials are used instead) nothing is shown.
 */
export function AdSlot() {
  const { t } = useTranslation();
  const user = useSession((s) => s.user);
  const ref = useRef<HTMLModElement>(null);
  const client = import.meta.env.VITE_ADSENSE_CLIENT as string | undefined;
  const slot = import.meta.env.VITE_ADSENSE_SLOT as string | undefined;
  const premium = user?.isPremium ?? false;
  const useAdsense = !premium && !isNative && !!client && !!slot;

  useEffect(() => {
    if (!useAdsense || !client) return;
    loadAdsense(client);
    try {
      (window.adsbygoogle = window.adsbygoogle ?? []).push({});
    } catch {
      /* ad blockers */
    }
  }, [useAdsense, client]);

  // Portals forbid third-party ads.
  if (!useAdsense || PORTAL) return null;
  return (
    <div className="ad-slot" aria-label={t('ads.label')}>
      <span className="label">{t('ads.label')}</span>
      <ins ref={ref} className="adsbygoogle" style={{ display: 'block', width: '100%' }} data-ad-client={client} data-ad-slot={slot} data-ad-format="auto" data-full-width-responsive="true" />
    </div>
  );
}
